import { Injectable } from '@angular/core';
import { AuthService } from '../auth-service/auth-service.service';
import { CraftConfiguration, DbCraft } from 'src/app/shared/models/craft.model';
import { TransportConfiguration } from 'src/app/shared/models/transport.model';
import { LocalizationLanguage } from 'src/app/shared/models/localization.model';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FirestoreService } from '../firestore/firestore.service';
import { AfmUser } from 'src/app/shared/models/afm.models';
import { PriceFavoritesList } from 'src/app/shared/models/price-favorites.model';
import { Location } from 'src/app/shared/models/location.model';
import { getAllRoyalMarketLocations } from 'src/app/shared/constants/locations.constant';
import { getServerName } from 'src/app/shared/constants/servers.constant';
import { MAX_FAVORITE_LIST } from 'src/app/shared/constants/config.constants';
import { PriceConfiguration } from 'src/app/shared/models/price.model';
import { FlipperConfiguration, MarketFlip } from 'src/app/shared/models/flipper.model';
import { LOCALIZATION_LANGUAGES } from 'src/app/shared/constants/localization.constant';
import { FarmConfiguration } from 'src/app/shared/models/farming-models/farm-models';
import { PathfinderConfiguration } from 'src/app/shared/models/pathfinder.models';
import { AfmLogsService } from '../afm-logs/afm-logs.service';
import { AnalyticsService } from '../analytics/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private _afmUser: AfmUser;
  private afmUserSubject = new ReplaySubject<AfmUser>();
  afmUser$: Observable<AfmUser> = this.afmUserSubject.asObservable();
  private userCraftsWereLoaded: boolean = false;
  private userCrafts: DbCraft[] = [];
  private userCraftsSubject = new BehaviorSubject<DbCraft[]>(this.userCrafts);
  userCrafts$: Observable<DbCraft[]> = this.userCraftsSubject.asObservable();
  private userCraftTotalsSubject = new BehaviorSubject<{ count: number; totalIncome: number; totalCost: number; totalProfit: number }>({
    count: 0,
    totalIncome: 0,
    totalCost: 0,
    totalProfit: 0,
  });
  userCraftTotals$: Observable<{ count: number; totalIncome: number; totalCost: number; totalProfit: number }> = this.userCraftTotalsSubject.asObservable();
  private userThemeSubject = new BehaviorSubject<string>('');
  userTheme$: Observable<string> = this.userThemeSubject.asObservable();
  private userServerSubject = new BehaviorSubject<string>('');
  userServer$: Observable<string> = this.userServerSubject.asObservable();
  private userLanguageSubject = new BehaviorSubject<LocalizationLanguage>(LOCALIZATION_LANGUAGES.EN_US);
  userLanguage$: Observable<LocalizationLanguage> = this.userLanguageSubject.asObservable();
  private userSelectedCitiesSubject = new BehaviorSubject<Location[]>(getAllRoyalMarketLocations());
  userSelectedCities$: Observable<Location[]> = this.userSelectedCitiesSubject.asObservable();
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedInSubject.asObservable();
  get isLoggedIn(): boolean {
    return this.isLoggedInSubject.value;
  }

  //#region CONSTRUCTOR
  constructor(
    private authService: AuthService,
    private firestoreService: FirestoreService,
    private snackBar: MatSnackBar,
    private afmLogsService: AfmLogsService,
    private analyticsService: AnalyticsService
  ) {
    this._afmUser = new AfmUser('tempUser', new Date());
    this.userThemeSubject.next(this._afmUser.afmUserSettings.theme);
    this.userServerSubject.next(this._afmUser.afmUserSettings.server);
    this.userLanguageSubject.next(this._afmUser.afmUserSettings.language);
    this.userSelectedCitiesSubject.next(this._afmUser.afmUserSettings.selectedCities);

    authService.user$.subscribe(async (aUser) => {
      if (aUser) {
        this._afmUser = await firestoreService.getUser(aUser);
        this.isLoggedInSubject.next(true);
        this.afmLogsService.logSiteUse(`User logged in: ${this._afmUser.email}`);
        this.analyticsService.sendLoginEvent('Google');
      } else {
        this._afmUser = new AfmUser('tempUser', new Date());
        this.isLoggedInSubject.next(false);
      }
      this.afmUserSubject.next(this._afmUser);
      this.userThemeSubject.next(this._afmUser.afmUserSettings.theme);
      this.userServerSubject.next(this._afmUser.afmUserSettings.server);
      this.userLanguageSubject.next(this._afmUser.afmUserSettings.language);
      this.userSelectedCitiesSubject.next(this._afmUser.afmUserSettings.selectedCities);
      this.userCraftsWereLoaded = false;
      this.userCrafts = [];
      this.userCraftsSubject.next(this.userCrafts);
      this.userCraftTotalsSubject.next({ count: 0, totalIncome: 0, totalCost: 0, totalProfit: 0 });
    });
  }

  //#region USER ACCOUNT
  async deleteAccount(): Promise<void> {
    if (this.isLoggedIn) {
      try {
        const resultData = await this.firestoreService.deleteUserData(this._afmUser);
        if (resultData) {
          try {
            this.afmLogsService.logAccountInfo('User requested account deletion.');
            await this.authService.deleteAccount();
            this.snackBar.open('User account and data deleted successfully', undefined, { duration: 3000 });
          } catch (error) {
            console.error('Failed to delete account:', error);
            this.snackBar.open('Failed to delete account', undefined, { duration: 3000 });
          }
        } else {
          this.snackBar.open('Failed to delete data and account', undefined, { duration: 3000 });
        }
      } catch (error) {
        console.error('Failed to delete user data:', error);
        this.snackBar.open('Failed to delete data and account', undefined, { duration: 3000 });
      }
    } else {
      this.snackBar.open('Failed: user is not logged in!', undefined, { duration: 3000 });
    }
  }

  //#region USER LANGUAGE
  getUserLanguage(): LocalizationLanguage {
    return this._afmUser.afmUserSettings.language;
  }
  async setUserLanguage(userLanguage: LocalizationLanguage): Promise<void> {
    this._afmUser.afmUserSettings.language = userLanguage;
    this.userLanguageSubject.next(this._afmUser.afmUserSettings.language);
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User changed language to ${userLanguage.id}`);
      await this.firestoreService.updateLanguage(this._afmUser, userLanguage);
    }
    this.snackBar.open(`Language Changed to ${userLanguage.name}`, undefined, { duration: 2000 });
  }

  //#region USER SERVER
  getUserServer(): string {
    return this._afmUser.afmUserSettings.server;
  }

  async setUserServer(server: string): Promise<void> {
    this._afmUser.afmUserSettings.server = server;
    this.userServerSubject.next(this._afmUser.afmUserSettings.server);
    this.snackBar.open(`Server set to ${getServerName(server)}`, undefined, { duration: 2000 });
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User changed server to ${server}`);
      await this.firestoreService.updateServer(this._afmUser, server);
    }
  }

  //#region USER THEME
  getUserTheme(): string {
    return this._afmUser.afmUserSettings.theme;
  }
  async setUserTheme(theme: string): Promise<void> {
    this._afmUser.afmUserSettings.theme = theme;
    this.userThemeSubject.next(this._afmUser.afmUserSettings.theme);
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User changed theme to ${theme}`);
      await this.firestoreService.updateTheme(this._afmUser, theme);
    }
  }

  //#region USER PRICE FAVORITES LISTS
  getUserPriceFavoritesLists(): PriceFavoritesList[] {
    return this._afmUser.priceFavoritesLists;
  }
  async addOrUpdateUserPriceFavoritesList(list: PriceFavoritesList, isEdit: boolean): Promise<void> {
    if (isEdit) {
      const existingList = this._afmUser.priceFavoritesLists.find((x) => x.name === list.name);
      if (existingList) {
        if (this.isLoggedIn) await this.firestoreService.deletePriceFavoritesList(this._afmUser, existingList);
        existingList.itemsIds = list.itemsIds;
        if (this.isLoggedIn) await this.firestoreService.addPriceFavoritesList(this._afmUser, existingList);
        this.snackBar.open('Favorite List Edited', undefined, { duration: 2000 });
      }
      return;
    }
    if (this._afmUser.priceFavoritesLists.length >= MAX_FAVORITE_LIST) {
      this.snackBar.open(`You can't have more than ${MAX_FAVORITE_LIST} Favorites Lists`, undefined, { duration: 2000 });
      return;
    }
    let name = list.name;
    let existingNames = this._afmUser.priceFavoritesLists.map((l) => l.name);
    let counter = 1;
    while (existingNames.includes(name)) {
      name = `${list.name}${counter}`;
      counter++;
    }
    let newList = { ...list, name: name };
    this._afmUser.priceFavoritesLists.push(newList);
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User created a new price favorites list: ${newList.name}`);
      await this.firestoreService.addPriceFavoritesList(this._afmUser, newList);
      this.snackBar.open('Favorite List Added', undefined, { duration: 2000 });
    }
  }

  async deleteUserPriceFavoritesList(list: PriceFavoritesList): Promise<void> {
    this._afmUser.priceFavoritesLists = this._afmUser.priceFavoritesLists.filter((x) => x.name != list.name);
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User deleted a price favorites list: ${list.name}`);
      await this.firestoreService.deletePriceFavoritesList(this._afmUser, list);
      this.snackBar.open('Deleted Favorite List', undefined, { duration: 2000 });
    }
  }

  //#region USER SELECTED CITIES
  toggleUserSelectedCity(city: Location): void {
    if (this._afmUser.afmUserSettings.selectedCities.includes(city)) {
      this._afmUser.afmUserSettings.selectedCities = this._afmUser.afmUserSettings.selectedCities.filter((c) => c !== city);
    } else {
      this._afmUser.afmUserSettings.selectedCities.push(city);
    }
    this.userSelectedCitiesSubject.next(this._afmUser.afmUserSettings.selectedCities);
  }

  async updateUserSelectedCities(): Promise<void> {
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User updated selected cities: ${this._afmUser.afmUserSettings.selectedCities.map((x) => x.name).join(', ')}`);
      await this.firestoreService.updateSelectedCities(this._afmUser, this._afmUser.afmUserSettings.selectedCities);
      this.snackBar.open('Saved Selected Cities to Database', undefined, { duration: 2000 });
    } else {
      this.snackBar.open('You must be logged in to save your selected cities', undefined, { duration: 2000 });
    }
  }

  //#region USER CRAFTS
  async ensureUserCraftsLoaded(amount: number = 50): Promise<void> {
    if (this.isLoggedIn && !this.userCraftsWereLoaded) {
      this.userCrafts = await this.getUserCrafts(amount);
      this.userCraftsSubject.next(this.userCrafts);
      this.userCraftTotalsSubject.next(await this.getUserCraftsTotals());
      this.userCraftsWereLoaded = true;
    }
  }

  async getUserCrafts(amount: number = 50): Promise<DbCraft[]> {
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User requested his crafts from the database`);
      return this.firestoreService.getUserCrafts(this._afmUser, amount);
    } else {
      return [];
    }
  }

  private async getUserCraftsTotals(): Promise<{ count: number; totalIncome: number; totalCost: number; totalProfit: number }> {
    if (this.isLoggedIn) {
      const result = await this.firestoreService.getUserCraftsTotals(this._afmUser);
      const count = result.count;
      const totalIncome = result.totalIncome;
      const totalCost = result.totalCost;
      const totalResult = totalIncome - totalCost;
      return { count, totalIncome, totalCost, totalProfit: totalResult };
    } else {
      return { count: 0, totalIncome: 0, totalCost: 0, totalProfit: 0 };
    }
  }

  async addUserCraft(craft: DbCraft): Promise<void> {
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User added a new craft: ${craft.productUniqueName}`);
      const ref = await this.firestoreService.addCraft(this._afmUser, craft);
      craft.craftId = ref.id;
      this.userCrafts.push(craft);
      this.userCraftsSubject.next(this.userCrafts);
      this.userCraftTotalsSubject.next(await this.getUserCraftsTotals());
      this.snackBar.open('Saved Craft to Database', undefined, { duration: 2000 });
    }
  }

  async deleteUserCraft(craft: DbCraft): Promise<void> {
    this.userCrafts = this.userCrafts.filter((x) => !(x.productUniqueName === craft.productUniqueName && x.date === craft.date));
    this.userCraftsSubject.next(this.userCrafts);
    if (this.isLoggedIn) {
      this.afmLogsService.logSiteUse(`User deleted a craft: ${craft.productUniqueName}`);
      await this.firestoreService.deleteCraft(this._afmUser, craft);
      this.userCraftTotalsSubject.next(await this.getUserCraftsTotals());
      this.snackBar.open('Deleted Craft from Database', undefined, { duration: 2000 });
    }
  }

  //#region FARM CONFIG
  getUserFarmConfig(): FarmConfiguration {
    return this._afmUser.afmUserSettings.farmConfiguration;
  }

  async updateUserFarmConfig(config: FarmConfiguration, specMap: Map<string, number>): Promise<void> {
    if (!this.isLoggedIn) {
      this.snackBar.open('Cannot update config: user not logged in', undefined, { duration: 2000 });
      return;
    }
    this.afmLogsService.logSiteUse(`User updated farm config`);
    await this.firestoreService.updateFarmConfig(this._afmUser, config, specMap);
    this.snackBar.open('Saved Farm Config to Database', undefined, { duration: 2000 });
  }

  //#region CRAFT CONFIG
  getUserCraftConfig(): CraftConfiguration {
    return this._afmUser.afmUserSettings.craftingConfiguration;
  }

  async updateUserCraftConfig(config: CraftConfiguration, specMap: Map<string, number>): Promise<void> {
    if (!this.isLoggedIn) {
      this.snackBar.open('Cannot update config: user not logged in', undefined, { duration: 2000 });
      return;
    }
    this.afmLogsService.logSiteUse(`User updated craft config`);
    await this.firestoreService.updateCraftConfig(this._afmUser, config, specMap);
    this.snackBar.open('Saved Craft Config to Database', undefined, { duration: 2000 });
  }

  //#region TRANSPORT CONFIG
  async updateUserTransportConfig(config: TransportConfiguration): Promise<void> {
    if (!this.isLoggedIn) {
      this.snackBar.open('Cannot update config: user not logged in', undefined, { duration: 2000 });
      return;
    }
    this.afmLogsService.logSiteUse(`User updated transport config`);
    await this.firestoreService.updateTransportConfig(this._afmUser, config);
    this.snackBar.open('Saved Transport Config to Database', undefined, { duration: 2000 });
  }

  getUserTransportConfig(): TransportConfiguration {
    return this._afmUser.afmUserSettings.transportConfiguration;
  }

  //#region USER PRICE CONFIG
  async updateUserPriceConfig(config: PriceConfiguration): Promise<void> {
    if (!this.isLoggedIn) {
      this.snackBar.open('Cannot update config: user not logged in', undefined, { duration: 2000 });
      return;
    }
    this.afmLogsService.logSiteUse(`User updated price config`);
    await this.firestoreService.updatePriceConfig(this._afmUser, config);
    this.snackBar.open('Saved Price Config to Database', undefined, { duration: 2000 });
  }

  getUserPriceConfig(): PriceConfiguration {
    return this._afmUser.afmUserSettings.priceConfiguration;
  }

  //#region USER PATHFINDER CONFIG
  async updateUserPathfinderConfig(config: PathfinderConfiguration): Promise<void> {
    if (!this.isLoggedIn) {
      this.snackBar.open('Cannot update config: user not logged in', undefined, { duration: 2000 });
      return;
    }
    this.afmLogsService.logSiteUse(`User updated pathfinder config`);
    await this.firestoreService.updatePathfinderConfig(this._afmUser, config);
    this.snackBar.open('Saved Pathfinder Config to Database', undefined, { duration: 2000 });
  }

  getUserPathfinderConfig(): PathfinderConfiguration {
    return this._afmUser.afmUserSettings.pathfinderConfiguration;
  }

  //#region USER FLIPPER CONFIG
  async updateUserFlipperConfig(config: FlipperConfiguration): Promise<void> {
    if (!this.isLoggedIn) {
      this.snackBar.open('Cannot update config: user not logged in', undefined, { duration: 2000 });
      return;
    }
    this.afmLogsService.logSiteUse(`User updated flipper config`);
    await this.firestoreService.updateFlipperConfig(this._afmUser, config);
    this.snackBar.open('Saved Flipper Config to Database', undefined, { duration: 2000 });
  }

  getUserFlipperConfig(): FlipperConfiguration {
    return this._afmUser.afmUserSettings.flipperConfiguration;
  }

  //#region USER SPEC
  getUserSpecMap(): Map<string, number> {
    return this._afmUser.userSpecMap;
  }

  setUserSpec(id: string, level: number): void {
    this._afmUser.userSpecMap.set(id, level);
  }

  //#region USER CONSUMED FLIPS
  getUserConsumedFlips(): MarketFlip[] {
    return this._afmUser.consumedFlips;
  }
  addUserConsumedFlip(flip: MarketFlip): void {
    if (!this._afmUser.consumedFlips.some((existingFlip) => existingFlip.id === flip.id)) {
      this._afmUser.consumedFlips.push(flip);
    }
  }
  removeUserConsumedFlip(flip: MarketFlip): void {
    this._afmUser.consumedFlips = this._afmUser.consumedFlips.filter((existingFlip) => existingFlip.id !== flip.id);
  }
}
